import React from 'react';
import Navbar from '../../components/Common/Navbar/Navbar';
import Footer from '../../components/Common/Footer/Footer';
import useSectionRefs from '../../hooks/useSectionRefs';
import { Sparkles as SparklesComp } from '../../components/ui/sparkles-pricing';
import { Card, CardContent, CardHeader } from '../../components/ui/card-pricing';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { getApiUrl, API_ENDPOINTS } from '../../utils/api';

type ContactFormData = {
  company?: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  communication: 'Email' | 'Phone';
};

function SectionHeader({ label, title }: { label: string; title: string }) {
  return (
    <div className="tw-mb-6">
      <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/5 tw-px-3 tw-py-1 tw-ring-1 tw-ring-white/10">
        <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-cyan" />
        <span className="tw-text-xs tw-font-medium tw-text-white/80">{label}</span>
      </div>
      <h3 className="tw-mt-3 tw-text-2xl tw-font-semibold tw-bg-gradient-to-r tw-from-white tw-to-white/70 tw-bg-clip-text tw-text-transparent">
        {title}
      </h3>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="tw-flex tw-items-start tw-gap-2">
      <span className="tw-h-2.5 tw-w-2.5 tw-bg-neutral-500 tw-rounded-full tw-mt-1.5 tw-flex-shrink-0"></span>
      <span className="tw-text-sm tw-text-gray-300 tw-font-[550] tw-text-left">{children}</span>
    </li>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-border tw-border-neutral-800/60 tw-rounded-2xl tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-text-white tw-p-6">
      {children}
    </div>
  );
}

export default function OfferMigration1() {
  const sectionRefs = useSectionRefs();
  const [stickyTop, setStickyTop] = React.useState<number>(96);

  React.useEffect(() => {
    const computeTop = () => {
      const navbar = (document.querySelector('nav.header-nav') ||
        document.querySelector('.navbar') ||
        document.querySelector('nav')) as HTMLElement | null;
      const navH = navbar?.offsetHeight ?? 60;
      setStickyTop(navH + 16);
    };
    computeTop();
    window.addEventListener('resize', computeTop);
    return () => window.removeEventListener('resize', computeTop);
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ContactFormData>({
    defaultValues: {
      message:
        "Je souhaite un Assessment Migration Azure (forfait 2 jours). Merci de me recontacter.",
    },
  });

  const [submissionError, setSubmissionError] = React.useState<string | null>(null);
  const { isReady } = useRecaptcha();

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setSubmissionError(null);
    if (!isReady || !window.grecaptcha) {
      setSubmissionError("La sécurité reCAPTCHA n'est pas encore prête. Veuillez réessayer dans quelques secondes.");
      return;
    }

    try {
      const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY!, {
        action: 'contact_submit',
      });
      const apiUrl = getApiUrl(API_ENDPOINTS.CONTACT);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, recaptchaToken: token, source: 'OfferMigration1' }),
      });
      if (!response.ok) {
        const text = await response.text();
        try {
          const err = JSON.parse(text);
          throw new Error(err.message || 'Erreur lors de l\'envoi.');
        } catch {
          throw new Error('Erreur lors de l\'envoi.');
        }
      }
      reset();
    } catch (e: any) {
      setSubmissionError(e?.message || "Une erreur est survenue. Veuillez réessayer.");
    }
  };

  return (
    <div className="tw-relative tw-bg-black tw-min-h-screen">
      {/* Background Effects */}
      <div className="tw-absolute tw-inset-0 tw-pointer-events-none">
        <div className="tw-absolute tw-top-0 tw-h-96 tw-w-full tw-overflow-hidden [mask-image:radial-gradient(50%_50%,white,transparent)]">
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-bg-[linear-gradient(to_right,#ffffff2c_1px,transparent_1px),linear-gradient(to_bottom,#3a3a3a01_1px,transparent_1px)] tw-bg-[size:70px_80px]"></div>
          <SparklesComp
            density={1800}
            speed={1}
            color="#FFFFFF"
            className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-full tw-w-full [mask-image:radial-gradient(50%_50%,white,transparent_85%)]"
          />
        </div>

        <div className="tw-absolute tw-left-0 tw-top-[-114px] tw-w-full tw-h-[100vh] tw-overflow-hidden">
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-rounded-full"
            style={{
              border: '200px solid var(--gradient-color)',
              filter: 'blur(92px)',
              WebkitFilter: 'blur(92px)',
            }}
          />
        </div>

        <div
          className="tw-absolute tw-top-0 tw-left-[10%] tw-right-[10%] tw-w-[80%] tw-h-full"
          style={{
            backgroundImage: `radial-gradient(circle at center, var(--gradient-color) 0%, transparent 70%)`,
            opacity: 0.6,
            mixBlendMode: 'multiply',
          }}
        />

        <div
          className="tw-absolute tw-top-[60%] tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-[1400px] tw-h-[800px]"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(49, 49, 245, 0.6) 0%, rgba(49, 49, 245, 0.3) 40%, rgba(49, 49, 245, 0.1) 70%, transparent 100%)',
            filter: 'blur(70px)',
            borderRadius: '50%',
          }}
        />
      </div>

      {/* Navbar */}
      <div className="tw-relative tw-z-50">
        <Navbar sectionRefs={sectionRefs} openContactModal={() => {}} closeContactModal={() => {}} />
      </div>

      {/* Content */}
      <section id="start" className="section dark tw-relative tw-z-10" ref={sectionRefs.start}>
        <div className="tw-relative tw-z-10 tw-mx-auto tw-max-w-5xl tw-px-4 md:tw-px-6 tw-pt-32 tw-pb-24">
          <div className="tw-mx-auto tw-mb-10 tw-max-w-3xl tw-text-center">
            <p className="tw-text-sm tw-uppercase tw-tracking-wide tw-text-white/70">
              Offre de migration cloud
            </p>
            <h1 className="tw-mt-2 tw-text-4xl tw-font-semibold tw-bg-gradient-to-r tw-from-white tw-to-white/70 tw-bg-clip-text tw-text-transparent">
              Assessment Migration Azure – Forfait 2 jours
            </h1>
            <p className="tw-mt-3 tw-text-white/80">
              Vision claire en 2 jours: faisabilité, coûts, plan par vagues et risques clés.
            </p>
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-10 lg:tw-grid-cols-3">
            {/* Left: Offer details */}
            <div className="tw-space-y-10 lg:tw-col-span-2">
              <Card className="tw-relative tw-text-white tw-border-neutral-800/80 tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-z-20">
                <CardHeader className="tw-text-left tw-pb-0">
                  <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/5 tw-px-3 tw-py-1 tw-ring-1 tw-ring-white/10">
                    <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-cyan" />
                    <span className="tw-text-xs tw-font-medium tw-text-white/80">Détail du forfait</span>
                  </div>
                </CardHeader>
                <CardContent className="tw-pt-4">
                  <SectionHeader label="Objectif" title="Ce que vous obtenez en 2 jours" />
                  <p className="tw-text-gray-300 tw-font-[550]">
                    Produire une vision claire de la faisabilité, du coût, du plan de migration par vagues et des risques/prérequis majeurs,
                    en s’appuyant sur les pratiques CAF et les sorties standard d’Azure Migrate.
                  </p>

                  <div className="tw-h-px tw-bg-neutral-800/60 tw-my-8" />

                  <SectionHeader label="Périmètre" title="Périmètre minimum couvert" />
                  <ul className="tw-space-y-3">
                    <Bullet>Atelier de cadrage et collecte rapide d’inventaire/sources</Bullet>
                    <Bullet>Hypothèses de sizing “as‑is” ou point-in-time</Bullet>
                    <Bullet>Premières estimations de coûts et options FinOps</Bullet>
                    <Bullet>Executive Summary: go/no‑go et étapes suivantes</Bullet>
                  </ul>

                  <div className="tw-h-px tw-bg-neutral-800/60 tw-my-8" />

                  <SectionHeader label="Méthodologie" title="Jour par jour" />
                  <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
                    <div>
                      <p className="tw-text-white tw-font-semibold tw-mb-2">Jour 1 – Stratégie et découverte</p>
                      <ul className="tw-space-y-2">
                        <Bullet>Ateliers de justification et résultats attendus</Bullet>
                        <Bullet>Recensement des workloads in-scope</Bullet>
                        <Bullet>Premiers regroupements applicatifs et dépendances connues</Bullet>
                        <Bullet>Introduction Azure Migrate et paramètres d’évaluation (CAF « Plan »)</Bullet>
                      </ul>
                    </div>
                    <div>
                      <p className="tw-text-white tw-font-semibold tw-mb-2">Jour 2 – Analyse et restitution</p>
                      <ul className="tw-space-y-2">
                        <Bullet>Readiness/rationalisation initiale (5R), sizing as‑is ou performance‑based</Bullet>
                        <Bullet>Première estimation mensuelle des coûts</Bullet>
                        <Bullet>Plan de migration par vagues, risques et quick wins</Bullet>
                        <Bullet>Restitution exécutive et plan d’action</Bullet>
                      </ul>
                    </div>
                  </div>

                  <div className="tw-h-px tw-bg-neutral-800/60 tw-my-8" />

                  <SectionHeader label="Livrables" title="Livrables du forfait 2 jours" />
                  <ul className="tw-space-y-2">
                    <Bullet>
                      Rapport d’évaluation: readiness Azure par groupe/VM, tailles cibles et fourchette de coûts mensuels, avec hypothèses documentées
                    </Bullet>
                    <Bullet>
                      Roadmap de migration: séquencement par vagues (CAF), critères d’éligibilité, contraintes d’arrêt, dépendances critiques
                    </Bullet>
                    <Bullet>
                      Estimation financière: scénarios de coûts avec options d’optimisation initiales (right‑size, hybrid benefit, réservations)
                    </Bullet>
                    <Bullet>
                      Executive Summary: risques, investissements attendus, bénéfices et prochaines étapes
                    </Bullet>
                  </ul>

                  <div className="tw-h-px tw-bg-neutral-800/60 tw-my-8" />

                  <SectionHeader label="Add‑ons" title="Options d’approfondissement" />
                  <ul className="tw-space-y-2">
                    <Bullet>Dépendances applicatives: analyse agent‑based (MMA/Service Map) ou agentless</Bullet>
                    <Bullet>Baseline de performance: collecte étendue pour right‑sizing fiable</Bullet>
                    <Bullet>FinOps+ TCO: comparatifs on‑prem vs Azure, multi‑scénarios</Bullet>
                    <Bullet>Sécurité et coûts associés: inclusion explicite des coûts de sécurité/monitoring</Bullet>
                    <Bullet>Readiness 1–2 semaines: interviews, mappings détaillés, roadmap granulaire</Bullet>
                    <Bullet>Évaluation 3–6 semaines: rationalisation (5R), archi cible et plan détaillé</Bullet>
                    <Bullet>Pré‑requis d’accès: inventaire minimal, accès lecture, ou import CSV</Bullet>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Right: Sticky contact form */}
            <div className="tw-space-y-4">
              <div className="tw-sticky" style={{ top: stickyTop }}>
                <Card className="tw-relative tw-text-white tw-border-neutral-800/80 tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-z-20">
                  <CardHeader className="tw-text-left tw-pb-2">
                    <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/5 tw-px-3 tw-py-1 tw-ring-1 tw-ring-white/10">
                      <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-cyan" />
                      <span className="tw-text-xs tw-font-medium tw-text-white/80">Démarrer l’évaluation</span>
                    </div>
                    <h4 className="tw-text-xl tw-font-semibold">Assessment Migration Azure</h4>
                    <p className="tw-text-sm tw-text-gray-400 tw-font-[550] tw-mt-1">Forfait fixe — 2 jours</p>
                  </CardHeader>
                  <CardContent>
                    {isSubmitSuccessful && (
                      <div className="tw-mb-4 tw-rounded-lg tw-bg-emerald-500/10 tw-px-3 tw-py-2 tw-text-emerald-300 tw-ring-1 tw-ring-emerald-400/30">
                        Message envoyé avec succès. Nous vous recontactons rapidement.
                      </div>
                    )}
                    {submissionError && (
                      <div className="tw-mb-4 tw-rounded-lg tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-red-300 tw-ring-1 tw-ring-red-400/30">
                        {submissionError}
                      </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
                      <div>
                        <label htmlFor="company" className="tw-sr-only">Société</label>
                        <input
                          id="company"
                          type="text"
                          {...register('company')}
                          className="tw-w-full tw-rounded-xl tw-border tw-border-neutral-700/60 tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-text-white placeholder:tw-text-white/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-cyan/60"
                          placeholder="Société (optionnel)"
                        />
                      </div>
                      <div>
                        <label htmlFor="name" className="tw-sr-only">Nom</label>
                        <input
                          id="name"
                          type="text"
                          {...register('name', { required: true })}
                          className={`tw-w-full tw-rounded-xl tw-border tw-border-neutral-700/60 tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-text-white placeholder:tw-text-white/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-cyan/60 ${errors.name ? 'tw-ring-2 tw-ring-red-400/70' : ''}`}
                          placeholder="Nom"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="tw-sr-only">E-mail</label>
                        <input
                          id="email"
                          type="email"
                          {...register('email', { required: true, pattern: /\S+@\S+\.[A-Za-z]{2,}/ })}
                          className={`tw-w-full tw-rounded-xl tw-border tw-border-neutral-700/60 tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-text-white placeholder:tw-text-white/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-cyan/60 ${errors.email ? 'tw-ring-2 tw-ring-red-400/70' : ''}`}
                          placeholder="E-mail"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="tw-sr-only">Téléphone</label>
                        <input
                          id="phone"
                          type="tel"
                          {...register('phone', { required: true })}
                          className={`tw-w-full tw-rounded-xl tw-border tw-border-neutral-700/60 tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-text-white placeholder:tw-text-white/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-cyan/60 ${errors.phone ? 'tw-ring-2 tw-ring-red-400/70' : ''}`}
                          placeholder="Téléphone"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="tw-sr-only">Message</label>
                        <textarea
                          id="message"
                          rows={3}
                          {...register('message', { required: true })}
                          className={`tw-w-full tw-rounded-xl tw-border tw-border-neutral-700/60 tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-text-white placeholder:tw-text-white/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-cyan/60 ${errors.message ? 'tw-ring-2 tw-ring-red-400/70' : ''}`}
                          placeholder="Votre contexte et vos objectifs (optionnel)"
                        />
                      </div>

                      <fieldset>
                        <legend className="tw-mb-2 tw-text-sm tw-text-white/80">Je souhaite être contacté par</legend>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-border tw-border-neutral-700/60">
                            <input type="radio" value="Email" {...register('communication', { required: true })} className="tw-h-4 tw-w-4" />
                            <span className="tw-text-white/90">E-mail</span>
                          </label>
                          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-bg-neutral-900/40 tw-px-4 tw-py-3 tw-border tw-border-neutral-700/60">
                            <input type="radio" value="Phone" {...register('communication', { required: true })} className="tw-h-4 tw-w-4" />
                            <span className="tw-text-white/90">Téléphone</span>
                          </label>
                        </div>
                        {errors.communication && (
                          <p className="tw-mt-2 tw-text-sm tw-text-red-300">Veuillez choisir une méthode de contact.</p>
                        )}
                      </fieldset>

                      <button
                        type="submit"
                        disabled={isSubmitting || !isReady}
                        className="tw-w-full tw-mb-2 tw-p-3 tw-text-base tw-rounded-xl tw-bg-gradient-to-t tw-from-neutral-950/50 tw-to-neutral-600/50 tw-backdrop-blur-sm tw-shadow-lg tw-shadow-neutral-900/30 tw-border tw-border-neutral-800/60 tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                      >
                        {isSubmitting ? 'Envoi en cours...' : !isReady ? 'Chargement...' : 'Demander mon évaluation'}
                      </button>
                      <p className="tw-text-xs tw-text-white/60">Protection anti‑spam activée (reCAPTCHA)</p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="footer" className="section dark tw-relative tw-z-10" ref={sectionRefs.newsletter}>
        <Footer />
      </section>
    </div>
  );
}
